import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Save, Package, Truck, FileDown, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { getFirstImage } from "@/hooks/useProducts";

const statusFlow = ["pending", "processing", "shipped", "completed", "cancelled", "refunded"];
const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
  processing: "bg-blue-100 text-blue-700 border-blue-300",
  shipped: "bg-purple-100 text-purple-700 border-purple-300",
  completed: "bg-green-100 text-green-700 border-green-300",
  cancelled: "bg-destructive/10 text-destructive border-destructive/30",
  refunded: "bg-muted text-muted-foreground border-border",
};

const AdminOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [rmLoading, setRmLoading] = useState<string | null>(null);
  const [apologyMsg, setApologyMsg] = useState("");
  const [refunding, setRefunding] = useState(false);
  

  const { data: order, isLoading } = useQuery({
    queryKey: ["admin-order", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("orders").select("*").eq("id", id!).single();
      if (error) throw error;
      setNewStatus(data.status);
      setAdminNotes((data as any).admin_notes || "");
      return data;
    },
    enabled: !!id,
  });

  const { data: items } = useQuery({
    queryKey: ["admin-order-items", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("order_items").select("*").eq("order_id", id!);
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("orders").update({ status: newStatus, admin_notes: adminNotes } as any).eq("id", id);
      if (error) throw error;
      await supabase.from("admin_activity_log").insert({
        user_email: (await supabase.auth.getUser()).data.user?.email || "unknown",
        action: "updated_order_status",
        entity_type: "order",
        entity_id: id,
        details: { old_status: order?.status, new_status: newStatus },
      });
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
      qc.invalidateQueries({ queryKey: ["admin-recent-orders"] });
      toast.success("Order updated");
    } catch (e: any) {
      toast.error(e.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const callRoyalMail = async (action: string) => {
    if (!id) return;
    setRmLoading(action);
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (action === "get_label") {
        const token = session?.access_token;
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/royal-mail`;
        const labelRes = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ action: "get_label", orderId: id }),
        });
        if (!labelRes.ok) {
          const err = await labelRes.json();
          throw new Error(err.error || "Failed to fetch label");
        }
        const contentType = labelRes.headers.get("Content-Type") || "";
        if (contentType.includes("application/json")) {
          const jsonData = await labelRes.json();
          if (jsonData.trackingNumber) {
            toast.success(`Tracking number saved: ${jsonData.trackingNumber}`);
          }
          if (jsonData.labelUnavailable) {
            toast.info("Print your label in Click & Drop — API label download isn't available for your account.", { duration: 6000 });
            window.open("https://parcel.royalmail.com/", "_blank");
          }
          qc.invalidateQueries({ queryKey: ["admin-order", id] });
          return;
        }
        const blob = await labelRes.blob();
        window.open(URL.createObjectURL(blob), "_blank");
        toast.success("Label downloaded");
        qc.invalidateQueries({ queryKey: ["admin-order", id] });
        return;
      }

      const res = await supabase.functions.invoke("royal-mail", {
        body: { action, orderId: id },
      });

      if (res.error) throw new Error(res.error.message);

      if (action === "create_order") {
        const trackingNumber = res.data?.trackingNumber;
        toast.success(trackingNumber
          ? `Order created! Tracking: ${trackingNumber}`
          : "Order sent to Royal Mail");
        qc.invalidateQueries({ queryKey: ["admin-order", id] });
      }
    } catch (e: any) {
      toast.error(e.message || "Royal Mail action failed");
    } finally {
      setRmLoading(null);
    }
  };

  const sendApology = async (test: boolean) => {
    if (!id || !order) return;
    if (!test && !confirm(`Cancel this order, mark it refunded and email ${order.customer_email}?`)) return;
    setRefunding(true);
    try {
      if (!test) {
        const { data: prior } = await supabase.from("admin_activity_log").select("id")
          .eq("entity_id", id).eq("action", "refund_apology_sent").limit(1);
        if (prior && prior.length) { toast.info("Apology email was already sent for this order."); return; }
      }
      const { error } = await supabase.functions.invoke("send-order-email", {
        body: { type: "refund_apology", orderId: id, customerName: order.customer_name, orderTotal: Number(order.total), message: apologyMsg, testRecipient: test },
      });
      if (error) throw error;
      if (test) { toast.success("Test sent to tedd@plymhair.co.uk"); return; }
      await supabase.from("orders").update({ status: "refunded" } as any).eq("id", id);
      await supabase.from("admin_activity_log").insert({
        user_email: (await supabase.auth.getUser()).data.user?.email || "unknown",
        action: "refund_apology_sent", entity_type: "order", entity_id: id,
        details: { old_status: order.status, new_status: "refunded", message: apologyMsg || null },
      });
      setNewStatus("refunded");
      qc.invalidateQueries({ queryKey: ["admin-order", id] });
      toast.success("Order refunded and apology email sent");
    } catch (e: any) {
      toast.error(e.message || "Failed to send apology");
    } finally {
      setRefunding(false);
    }
  };

  if (isLoading) return <div className="text-muted-foreground text-center py-20">Loading…</div>;
  if (!order) return <div className="text-center py-20">Order not found</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/orders")}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="font-display text-2xl font-bold">Order Details</h1>
          <p className="text-sm text-muted-foreground font-body">{order.id.slice(0, 8)}…</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-display font-semibold">Items</h2>
            </div>
            <div className="divide-y divide-border">
              {items?.map((item) => (
                <div key={item.id} className="flex items-center gap-4 px-5 py-3">
                  <img src={item.product_image || "/placeholder.svg"} alt="" className="w-12 h-12 rounded object-cover bg-muted" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium truncate">{item.product_name}</p>
                    {item.variant_label && <p className="text-xs text-muted-foreground font-body">{item.variant_label}</p>}
                  </div>
                  <p className="text-sm font-body text-muted-foreground">×{item.quantity}</p>
                  <p className="text-sm font-body font-semibold w-20 text-right">£{item.total.toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-border bg-secondary/30">
              <div className="flex justify-between font-body text-sm"><span>Subtotal</span><span>£{order.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between font-body text-sm font-bold mt-1"><span>Total</span><span>£{order.total.toFixed(2)}</span></div>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h2 className="font-display font-semibold mb-3">Admin Notes</h2>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Internal notes about this order…"
              className="w-full h-24 rounded-md border border-input bg-background px-3 py-2 text-sm font-body resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h2 className="font-display font-semibold mb-3">Status</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              {statusFlow.map((s) => (
                <button
                  key={s}
                  onClick={() => setNewStatus(s)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                    newStatus === s ? statusColors[s] + " ring-2 ring-ring" : "bg-secondary/50 text-muted-foreground border-border hover:bg-secondary"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>

          {/* Customer Info */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h2 className="font-display font-semibold mb-3">Customer</h2>
            <div className="space-y-2 text-sm font-body">
              <p><span className="text-muted-foreground">Name:</span> {order.customer_name || "—"}</p>
              <p><span className="text-muted-foreground">Email:</span> {order.customer_email || "—"}</p>
              <p><span className="text-muted-foreground">Shipping:</span> {order.shipping_address || "—"}</p>
            </div>
          </div>

          {/* Out of stock refund */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h2 className="font-display font-semibold mb-1">Cancel & refund (out of stock)</h2>
            <p className="text-xs text-muted-foreground font-body mb-3">
              Marks the order as refunded and emails the customer an apology. Refund the money in Stripe as usual.
            </p>
            <textarea
              value={apologyMsg}
              onChange={(e) => setApologyMsg(e.target.value)}
              maxLength={1000}
              placeholder="Optional personal message, e.g. We expect to restock in 2 weeks"
              className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm font-body resize-none mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div className="space-y-2">
              <Button variant="outline" className="w-full" disabled={refunding} onClick={() => sendApology(true)}>
                <Mail className="w-4 h-4 mr-1.5" />Send test to me
              </Button>
              <Button variant="destructive" className="w-full" disabled={refunding} onClick={() => sendApology(false)}>
                {refunding ? "Sending…" : "Cancel, refund & email customer"}
              </Button>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h2 className="font-display font-semibold mb-3">Timeline</h2>
            <div className="space-y-2 text-sm font-body">
              <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-muted-foreground" />Created: {format(new Date(order.created_at), "dd MMM yyyy, HH:mm")}</p>
              <p className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-muted-foreground" />Updated: {format(new Date(order.updated_at), "dd MMM yyyy, HH:mm")}</p>
            </div>
          </div>
          {/* Royal Mail */}
          <div className="bg-card rounded-lg border border-border p-5">
            <h2 className="font-display font-semibold mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" /> Royal Mail
            </h2>
            {(order as any).tracking_number && (
              <div className="mb-3 p-2 bg-secondary/50 rounded text-sm font-body">
                <span className="text-muted-foreground">Tracking:</span>{" "}
                <span className="font-mono font-medium">{(order as any).tracking_number}</span>
              </div>
            )}
            <div className="space-y-2">
              {order.status === "processing" && !(order as any).tracking_number ? (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  disabled={rmLoading !== null}
                  onClick={() => callRoyalMail("create_order")}
                >
                  <Truck className="w-4 h-4" />
                  {rmLoading === "create_order" ? "Sending…" : "Create Shipment"}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    disabled={rmLoading !== null}
                    onClick={() => callRoyalMail("get_label")}
                  >
                    <FileDown className="w-4 h-4" />
                    {rmLoading === "get_label" ? "Fetching…" : "Download Label"}
                  </Button>
                  {(order as any).tracking_number && (
                    <Button variant="outline" className="w-full justify-start gap-2" asChild>
                      <a
                        href={`https://www.royalmail.com/track-your-item#/tracking-results/${(order as any).tracking_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Track Parcel
                      </a>
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetail;
